import React from 'react';

import { cn } from 'src/component/ds/utils';

/* "Card - Vega" in Figma: 1px border, 16px padding, 8px gap between slots. */

function Card ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'ds-rounded-ds ds-border ds-border-border ds-bg-card ds-text-card-foreground',
        className
      )}
      {...props}
    />
  );
}

function CardHeader ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ds-flex ds-flex-col ds-gap-1.5 ds-p-4', className)} {...props} />;
}

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /* The heading level has to follow the page's outline, not the card's look --
   * a card nested under an <h1> needs an <h2> here. Styling is the className's
   * job, so the level is free to be whatever the outline requires. */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Heading = 'h3', ...props }, ref) => (
    <Heading
      ref={ref}
      className={cn('ds-text-ds-h4 ds-font-medium ds-text-foreground', className)}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('ds-text-ds-sm ds-text-muted-foreground', className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';

function CardContent ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('ds-p-4 ds-pt-0', className)} {...props} />;
}

function CardFooter ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('ds-flex ds-items-center ds-gap-2 ds-p-4 ds-pt-0', className)} {...props} />
  );
}

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
